'use strict';


import { Matrix3, MeshStandardMaterial } from 'three';


const vertex_vars_additionalMaterial = `
uniform mat3 aoMap2Transform;
varying vec2 vAoMap2Uv;
`;

const vertex_pars_additionalMaterial = `
#ifdef USE_AOMAP2
    vAoMap2Uv = ( aoMap2Transform * vec3( AOMAP2_UV, 1 ) ).xy;
#endif
`;

const fragment_vars_additionalMaterial = `
uniform float brightness;
uniform float contrast;
uniform float saturation;
uniform float hue;
uniform float aoMap2Intensity;
uniform sampler2D aoMap2;
varying vec2 vAoMap2Uv;
`;

const fragment_ao2_additionalMaterial = `
#ifdef USE_AOMAP2

	// reads channel R, compatible with a combined OcclusionRoughnessMetallic (RGB) texture
	float ambientOcclusion2 = ( texture2D( aoMap2, vAoMap2Uv ).r - 1.0 ) * aoMap2Intensity + 1.0;

	reflectedLight.indirectDiffuse *= ambientOcclusion2;

	#if defined( USE_CLEARCOAT )
		clearcoatSpecularIndirect *= ambientOcclusion2;
	#endif

	#if defined( USE_SHEEN )
		sheenSpecularIndirect *= ambientOcclusion2;
	#endif

	#if defined( USE_ENVMAP ) && defined( STANDARD )

		float dotNV2 = saturate( dot( geometryNormal, geometryViewDir ) );

		reflectedLight.indirectSpecular *= computeSpecularOcclusion( dotNV2, ambientOcclusion2, material.roughness );

	#endif

#endif
`;

const fragment_contrast_additionalMaterial = `
#ifdef USE_MAP

	vec4 sampledDiffuseColor = texture2D( map, vMapUv );

	#ifdef DECODE_VIDEO_TEXTURE

		// use inline sRGB decode until browsers properly support SRGB8_ALPHA8 with video textures (#26516)

		sampledDiffuseColor = vec4( mix( pow( sampledDiffuseColor.rgb * 0.9478672986 + vec3( 0.0521327014 ), vec3( 2.4 ) ), sampledDiffuseColor.rgb * 0.0773993808, vec3( lessThanEqual( sampledDiffuseColor.rgb, vec3( 0.04045 ) ) ) ), sampledDiffuseColor.w );
	
	#endif
	
	sampledDiffuseColor.rgb = AdjustHue(sampledDiffuseColor.rgb, hue);
	sampledDiffuseColor.rgb = AdjustSaturation(sampledDiffuseColor.rgb, saturation);
	sampledDiffuseColor.rgb = AdjustBrightness(sampledDiffuseColor.rgb, brightness);
    sampledDiffuseColor.rgb = AdjustContrast(sampledDiffuseColor.rgb, contrast);

	diffuseColor *= sampledDiffuseColor;

#endif
`;

const fragment_pars_additionalMaterial = `
vec3 AdjustBrightness(vec3 color, float factor) {
    return color + factor;
}

vec3 AdjustContrast(vec3 color, float factor) {
    return (color - 0.5) * factor + 0.5;
}

vec3 AdjustSaturation(vec3 color, float factor) {
    float lum = dot(color, vec3(0.299, 0.587, 0.114));
    return mix(vec3(lum), color, factor);
}

vec3 AdjustHue(vec3 color, float angle) {
    float c = cos(angle);
    float s = sin(angle);
    mat3 hueRotation = mat3(
        vec3(0.299 + 0.701 * c + 0.168 * s, 0.587 - 0.587 * c + 0.330 * s, 0.114 - 0.114 * c - 0.497 * s),
        vec3(0.299 - 0.299 * c - 0.328 * s, 0.587 + 0.413 * c + 0.035 * s, 0.114 - 0.114 * c + 0.292 * s),
        vec3(0.299 - 0.3 * c + 1.25 * s, 0.587 - 0.588 * c - 1.05 * s, 0.114 + 0.886 * c - 0.203 * s)
    );
    return hueRotation * color;
}
`;

export class AdditionalStandardMaterial extends MeshStandardMaterial{
	#shader;
	
	constructor (parameters) {
		super(parameters);
		
		this.contrast = 1;
		this.saturation = 1;
		this.hue = 0;
		this.brightness = 0;
		this.aoMap2 = null;
		this.aoMap2Intensity = 1;
		this.defines.USE_UV1 = '';
		this.defines.AOMAP2_UV = 'uv1';
		
		this.customPrograms = [this.upgradeShader.bind(this)];
	}
	
	upgradeShader(shader) {
		shader.defines.USE_UV1 = '';
		
		shader.uniforms.contrast = {
			value: this.contrast
		};
		shader.uniforms.saturation = {
			value: this.saturation
		};
		shader.uniforms.hue = {
			value: this.hue
		};
		shader.uniforms.brightness = {
			value: this.brightness
		};
		shader.uniforms.aoMap2 = {
			value: this.aoMap2
		};
		shader.uniforms.aoMap2Intensity = {
			value: this.aoMap2Intensity
		};
		shader.uniforms.aoMap2Transform = {
			value: this.aoMap2 ? this.aoMap2.matrix.clone() : new Matrix3()
		};
		
		if (this.aoMap2) {
			shader.defines.USE_AOMAP2 = true;
			this.defines.AOMAP2_UV = this.#getActiveChannel(this.aoMap2.channel);
		} else {
			delete shader.defines.USE_AOMAP2;
			this.defines.AOMAP2_UV = 'uv1';
		}
		
		shader.vertexShader = vertex_vars_additionalMaterial + shader.vertexShader;
		shader.vertexShader = shader.vertexShader.replace('#include <uv_vertex>',[
			vertex_pars_additionalMaterial,
			'#include <uv_vertex>'
		].join('\n'));
		
		shader.fragmentShader = fragment_vars_additionalMaterial + shader.fragmentShader;
		
		shader.fragmentShader = shader.fragmentShader.replace('#include <clipping_planes_pars_fragment>',[
			'#include <clipping_planes_pars_fragment>',
			fragment_pars_additionalMaterial
		].join('\n'));
		
		shader.fragmentShader = shader.fragmentShader.replace('#include <map_fragment>',[
			fragment_contrast_additionalMaterial
		].join('\n'));
		
		shader.fragmentShader = shader.fragmentShader.replace('#include <aomap_fragment>',[
			'#include <aomap_fragment>',
			fragment_ao2_additionalMaterial
		].join('\n'));
		
		this.#shader = shader;
	}
	
	#getActiveChannel(value) {
		if ( value === 0 ) return 'uv';
		
		return `uv${ value }`;
	}
	
}
